// Copyright 2026 Granite (granite.md). All rights reserved.
// Proprietary and confidential. Unauthorized copying is prohibited.

/** English UI strings: the staff admin console (routes/AdminPage.tsx). Merged into the catalog by messages.en.ts. */
export const adminConsole = {
  "shell.admin": "Admin",

  "admin.title": "Admin",
  "admin.description": "Granite staff only. Every change here is recorded in the staff log.",
  "admin.loading": "Loading",
  "admin.notAllowed": "This page is for Granite staff.",
  "admin.loadMore": "Load more",
  "admin.empty": "Nothing here.",
  "admin.search": "Search",
  "admin.cancel": "Cancel",
  "admin.save": "Save",
  "admin.never": "never",
  "admin.none": "—",
  "admin.back": "Admin",
  "admin.unlimited": "no end",

  "admin.tab.overview": "Overview",
  "admin.tab.users": "Users",
  "admin.tab.workspaces": "Workspaces",
  "admin.tab.vaults": "Vaults",
  "admin.tab.storage": "Storage",
  "admin.tab.coupons": "Coupons",
  "admin.tab.gifts": "Gifts",
  "admin.tab.log": "Staff log",

  "admin.overview.revenue": "Revenue",
  "admin.overview.revenueDescription": "Estimated from list prices, before discounts and store fees.",
  "admin.overview.mrr": "Monthly recurring revenue",
  "admin.overview.arr": "{amount} a year",
  "admin.overview.paying": "Paying subscriptions",
  "admin.overview.pastDue": "{count} with a failed payment",
  "admin.overview.trialing": "In a paid plan's trial",
  "admin.overview.gifted": "Gifted plans",
  "admin.overview.canceled": "Canceled in 30 days",
  "admin.overview.accounts": "Accounts",
  "admin.overview.users": "Total",
  "admin.overview.new1d": "New in 24 hours",
  "admin.overview.newUsers": "New in 7 days",
  "admin.overview.new30d": "New in 30 days",
  "admin.overview.deleted30d": "Deleted in 30 days",
  "admin.overview.active1d": "Active in 24 hours",
  "admin.overview.activeUsers": "Active in 7 days",
  "admin.overview.active30d": "Active in 30 days",
  "admin.overview.content": "Content",
  "admin.overview.workspaces": "Workspaces",
  "admin.overview.workspacesHint": "{fresh} new in 30 days, {trial} on trial",
  "admin.overview.vaults": "Vaults",
  "admin.overview.vaultsTrash": "{count} in the trash",
  "admin.overview.notes": "Notes",
  "admin.overview.attachments": "Other files",
  "admin.overview.storage": "Storage",
  "admin.overview.platforms": "Active by platform",
  "admin.overview.platformsDescription": "People active in the last 30 days. Someone on two platforms counts in both.",
  "admin.overview.platformOther": "Other",
  "admin.col.platform": "Platform",
  "admin.overview.signups": "Sign-ups, last 30 days",
  "admin.overview.sources": "Where sign-ups came from",
  "admin.overview.sourcesDescription": "Accounts made in the last 30 days, by the site or campaign that first brought the person to granite.md, or the app they signed up in.",
  "admin.overview.signupsDay": "{day}: {count}",
  "admin.overview.plans": "Plans in use",
  "admin.overview.plansDescription": "Subscriptions that give a plan now, by channel.",
  "admin.overview.noPlans": "No paid or gifted plans yet.",

  "admin.provider.stripe": "Web (Stripe)",
  "admin.provider.app_store": "App Store",
  "admin.provider.google_play": "Google Play",
  "admin.provider.internal": "Gift",

  "admin.plan.free": "Free",
  "admin.plan.pro": "Pro",
  "admin.plan.business": "Business",
  "admin.planStatus.free": "free",
  "admin.planStatus.trial": "trial",
  "admin.planStatus.active": "active",
  "admin.planStatus.past_due": "payment failed",
  "admin.planStatus.lapsed": "lapsed",

  "admin.col.account": "Account",
  "admin.col.plan": "Plan",
  "admin.col.storage": "Storage",
  "admin.col.vaults": "Vaults",
  "admin.col.lastSeen": "Last seen",
  "admin.col.created": "Created",
  "admin.col.source": "Came from",
  "admin.col.name": "Name",
  "admin.col.owner": "Owner",
  "admin.col.members": "Members",
  "admin.col.files": "Files",
  "admin.col.changes": "Changes",
  "admin.col.state": "State",
  "admin.col.code": "Code",
  "admin.col.discount": "Discount",
  "admin.col.redeemed": "Used",
  "admin.col.expires": "Expires",
  "admin.col.ends": "Ends",
  "admin.col.givenBy": "Given by",
  "admin.col.note": "Note",
  "admin.col.when": "When",
  "admin.col.who": "Who",
  "admin.col.action": "Action",
  "admin.col.details": "Details",
  "admin.col.limit": "Use of limit",
  "admin.col.role": "Role",
  "admin.col.channel": "Channel",
  "admin.col.joined": "Joined",

  "admin.users.searchPlaceholder": "Email, name, or ID",
  "admin.users.status": "Status",
  "admin.users.anyStatus": "Any status",
  "admin.status.active": "active",
  "admin.status.disabled": "disabled",
  "admin.status.deleted": "deleted",
  "admin.badge.staff": "staff",
  "admin.badge.unverified": "unconfirmed email",
  "admin.badge.locked": "locked",
  "admin.badge.twoFactor": "2FA",
  "admin.badge.deleted": "deleted",
  "admin.badge.trash": "in the trash",

  "admin.workspaces.searchPlaceholder": "Name, slug, or ID",
  "admin.vaults.searchPlaceholder": "Name or ID",
  "admin.vaults.state": "Show",
  "admin.vaults.stateAll": "All vaults",
  "admin.vaults.stateLive": "Not deleted",
  "admin.vaults.stateTrash": "In the trash",
  "admin.vaults.trash": "Move to trash",
  "admin.vaults.restore": "Restore",
  "admin.vaults.confirmTrash": "Move “{name}” to the trash? Its owner can no longer open it; it can be restored until {date}.",
  "admin.vaults.purges": "purged on {date}",

  "admin.storage.counted": "Stored (encrypted)",
  "admin.storage.countedAt": "Counted {date}",
  "admin.storage.owners": "Owners storing files",
  "admin.storage.files": "File versions (plain size)",
  "admin.storage.filesCount": "{count} versions",
  "admin.storage.recount": "Recount now",
  "admin.storage.recounted": "Recounted {count} owners.",
  "admin.storage.top": "Largest owners",
  "admin.storage.topDescription": "As of the last recount. Owners over their limit can read everything but cannot upload.",

  "admin.coupons.description": "Codes people type on the payment page. They are Stripe promotion codes; removing one stops new use, and subscriptions that already have the discount keep it.",
  "admin.coupons.add": "Add a coupon code",
  "admin.coupons.code": "Code",
  "admin.coupons.codeHelp": "3 to 40 letters, digits, dashes, or underscores. Saved in capitals.",
  "admin.coupons.kind": "Discount",
  "admin.coupons.percent": "Percentage",
  "admin.coupons.amount": "Amount (USD)",
  "admin.coupons.percentLabel": "Percent off",
  "admin.coupons.amountLabel": "Dollars off",
  "admin.coupons.duration": "Applies to",
  "admin.coupons.once": "The first payment",
  "admin.coupons.repeating": "Several months",
  "admin.coupons.forever": "Every payment",
  "admin.coupons.months": "Months",
  "admin.coupons.maxRedemptions": "Uses in total (optional)",
  "admin.coupons.expiresAt": "Stops working on (optional)",
  "admin.coupons.firstTimeOnly": "Only for people who never paid before",
  "admin.coupons.create": "Add code",
  "admin.coupons.remove": "Remove",
  "admin.coupons.confirmRemove": "Stop the code {code} from working?",
  "admin.coupons.inactive": "removed",
  "admin.coupons.percentOff": "{value}% off",
  "admin.coupons.amountOff": "{value} off",
  "admin.coupons.for.once": "first payment",
  "admin.coupons.for.repeating": "{count} months",
  "admin.coupons.for.forever": "every payment",
  "admin.coupons.of": "{used} of {max}",

  "admin.gifts.description": "Plans Granite gives away, never billed. They end on their date, or when you end them.",
  "admin.gifts.give": "Gift a plan",
  "admin.gifts.giveTitle": "Gift a plan",
  "admin.gifts.giveDescription": "Pro for a person, Business for a workspace. Refused while they already pay.",
  "admin.gifts.to": "To",
  "admin.gifts.toUser": "A person (Pro)",
  "admin.gifts.toWorkspace": "A workspace (Business)",
  "admin.gifts.find": "Find",
  "admin.gifts.findUser": "Email or name",
  "admin.gifts.findWorkspace": "Workspace name or slug",
  "admin.gifts.pick": "Choose",
  "admin.gifts.picked": "Gift for {name}",
  "admin.gifts.change": "Change",
  "admin.gifts.length": "For",
  "admin.gifts.month1": "1 month",
  "admin.gifts.month3": "3 months",
  "admin.gifts.month6": "6 months",
  "admin.gifts.month12": "12 months",
  "admin.gifts.forever": "No end",
  "admin.gifts.until": "Until a date",
  "admin.gifts.endsOn": "Ends on",
  "admin.gifts.note": "Note (why)",
  "admin.gifts.submit": "Give",
  "admin.gifts.end": "End now",
  "admin.gifts.confirmEnd": "End this gift now? {name} goes back to their own plan at once.",
  "admin.gifts.running": "running",
  "admin.gifts.ended": "ended",
  "admin.business.make": "Make business account",
  "admin.business.title": "Make business account",
  "admin.business.description": "Starts a workspace that {email} owns. They become a company account on the workspace's plan.",
  "admin.business.name": "Workspace name",
  "admin.business.gift": "Gift Business to the workspace",
  "admin.business.trial": "The workspace starts on the 30-day Business trial.",
  "admin.business.submit": "Make business account",

  "admin.log.description": "Every change made in this console, newest first.",

  "admin.user.title": "Account",
  "admin.user.joined": "joined {date}",
  "admin.user.actions": "Account",
  "admin.user.disable": "Disable account",
  "admin.user.enable": "Enable account",
  "admin.user.confirmDisable": "Disable {email}? They are signed out everywhere and cannot sign in until enabled again.",
  "admin.user.verify": "Confirm email",
  "admin.user.unlock": "Unlock sign-in",
  "admin.user.reset2fa": "Turn off 2FA",
  "admin.user.confirmReset2fa": "Turn off two-factor authentication for {email}? Do this only after confirming who is asking.",
  "admin.user.signOut": "Sign out everywhere",
  "admin.user.confirmSignOut": "Sign {email} out of every device, API token, and app?",
  "admin.user.done": "Done.",
  "admin.user.plan": "Plan and storage",
  "admin.user.storageNow": "{used} of {limit}",
  "admin.user.subscriptions": "Subscriptions",
  "admin.user.noSubscriptions": "No subscriptions.",
  "admin.user.vaults": "Vaults",
  "admin.user.workspaces": "Workspaces",
  "admin.user.noWorkspaces": "Not in any workspace.",
  "admin.user.devices": "Devices",
  "admin.user.noDevices": "Never signed in.",
  "admin.user.device": "{platform} · {version}",
  "admin.user.sessions": "{count} live sessions",
  "admin.user.signedOut": "signed out",
  "admin.user.activity": "Activity",
  "admin.user.activityDescription": "What they did, and what was done to their account.",
  "admin.user.activityFilter": "Actions starting with",
  "admin.user.allActions": "All actions",

  "admin.sub.periodEnd": "until {date}",
  "admin.sub.current": "gives the plan",
  "admin.sub.seats": "{count} seats",

  "admin.workspace.title": "Workspace",
  "admin.workspace.trial": "Free trial",
  "admin.workspace.trialDescription": "Business is free until this date (move it to extend the trial, or end it).",
  "admin.workspace.trialEnds": "Trial ends on",
  "admin.workspace.members": "Members",
  "admin.workspace.vaults": "Shared vaults",
  "admin.workspace.subscriptions": "Subscriptions",
  "admin.workspace.ownedBy": "owner {email}",
  "admin.workspace.deleted": "Deleted {date}",

  "error.not_admin": "This page is for Granite staff.",
  "error.admin_two_factor_required": "Turn on two-factor authentication in Settings to use the admin console.",
  "error.admin_email_unverified": "Confirm your email address to use the admin console.",
  "error.admin_unavailable": "The admin console is not set up on this server.",
  "error.gift_exists": "They already have a gifted plan. End it first.",
} as const;
